import { useState } from 'react'
import { faFileArrowUp, faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { useTranslationRankmi } from 'helpers/translations.helper'
import { isExcelFile } from 'helpers'
import AwesomeFileInput from 'components/ui/forms/field/awesomeFileInput/AwesomeFileInput'
import { FileTemplate } from 'components/ui/modals/UploadFile/V1/FileTemplate'
import UploadFile from 'api/commons/UploadFile'
import * as fromQuestionActions from 'store/actions/questions.action'
import { ErrorToast, SuccessToast } from 'components/ui/toast/toastsTemplates'
import { importFinish } from 'helpers/importProcess'

interface UploadQuestionProps {
  evaluationId: number
  instanceId: number
}

export const UploadQuestion = ({
  evaluationId,
  instanceId,
}: UploadQuestionProps) => {
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  const tUpload = useTranslationRankmi({
    prefix: 'administration.blocks.type.evaluation.modals.uploadQuestions',
  })
  const [error, setError] = useState<React.ReactNode>(null)
  const { t } = useTranslation()
  const handleClose = () => setShow(false)

  const reloadQuestions = () => {
    dispatch({
      type: fromQuestionActions.GET_QUESTIONS_LIST,
      instanceId,
    })
  }

  const handleFileUpload = async (file: File) => {
    setError(null)
    if (!isExcelFile(file)) {
      setTimeout(() => {
        setError(<>{tUpload('error')}</>)
      }, 500)
      return
    }

    handleClose()
    setLoading(true)
    try {
      const { data: id } = await UploadFile<string>(
        file,
        `/administration/block_evaluations/${evaluationId}/questions/upload`,
        {
          success: tUpload('toasts.importing'),
        }
      )

      // Waiting for the import to finish listening to the status of the import process in firebase
      await importFinish(id)

      SuccessToast(tUpload('toasts.success'))

      reloadQuestions()
    } catch (e) {
      ErrorToast(tUpload('toasts.error'))
    }
    setLoading(false)
  }

  return (
    <>
      <Button
        size="sm"
        onClick={() => {
          setShow(true)
        }}
        disabled={loading}
      >
        Importador de preguntas
        <FontAwesomeIcon
          className="ml-2"
          icon={loading ? faSpinner : faFileArrowUp}
          spin={loading}
        />
      </Button>
      <Modal size="lg" show={show} onHide={handleClose} centered>
        <Modal.Header closeButton className="ml-3 mr-3 mt-2 pb-2">
          <Modal.Title>{tUpload('title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="ml-3 mr-3">
          {tUpload('text')}
          <AwesomeFileInput
            multiFile={false}
            onChange={([newFile]) => {
              handleFileUpload(newFile)
            }}
            className="mt-3"
            error={!!error}
          />
          {error && (
            <p className={'lead small text-danger text-center'}>{error}</p>
          )}
          <div className="text-right">
            <FileTemplate
              label={tUpload('template')}
              link="https://rankmi-lms-prod.s3.amazonaws.com/templates/question-importer-template.xlsx"
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="ml-3 mr-3 mt-2">
          <Button size="sm" variant="secondary" onClick={handleClose}>
            {t('commons.buttons.close')}
          </Button>
          <Button size="sm" onClick={handleClose}>
            {t('commons.buttons.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
